import React, { useEffect, useState } from "react";
import { Badge, Button, Form, Spinner } from "react-bootstrap";
import weatherApi from "../../services/weatherAdminApi";

const emptyCampaign = {
  id: null, campaignName: "Rain Alert Display", campaignStatus: "DRAFT", title: "Rain Alert Display",
  shortMessage: "", detailedMessage: "", desktopImageUrl: "", mobileImageUrl: "",
  backgroundColor: "#EAF5FF", textColor: "#15395B", buttonText: "", buttonUrl: "",
  dismissible: true, displayFrequency: "ONCE_PER_SESSION", priority: 0, scopeType: "GLOBAL",
  branchId: null, weatherLocalityId: null, startAt: null, endAt: null,
  webCartEnabled: false, webCheckoutEnabled: false,
  mobileCartEnabled: false, mobileCheckoutEnabled: false,
};

const enabled = (value) => Number(value) === 1 || value === true;
const IMAGE_BASE_URL = String(
  process.env.REACT_APP_IMAGE_URL || ""
).replace(/\/+$/, "");

const getImageUrl = (imagePath) => {
  const path = String(imagePath || "").trim();

  if (!path) return "";

  // Backward compatibility for any existing full URLs
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${IMAGE_BASE_URL}/${path.replace(/^\/+/, "")}`;
};
const normalizeCampaign = (campaign) => campaign ? {
  ...emptyCampaign,
  id: campaign.id,
  campaignName: campaign.campaign_name || "Rain Alert Display",
  campaignStatus: campaign.campaign_status || "DRAFT",
  title: campaign.title || campaign.campaign_name || "Rain Alert Display",
  shortMessage: campaign.short_message || "",
  detailedMessage: campaign.detailed_message || "",
  desktopImageUrl: campaign.desktop_image_url || "",
  mobileImageUrl: campaign.mobile_image_url || "",
  backgroundColor: campaign.background_color || "#EAF5FF",
  textColor: campaign.text_color || "#15395B",
  buttonText: campaign.button_text || "",
  buttonUrl: campaign.button_url || "",
  dismissible: enabled(campaign.dismissible),
  displayFrequency: campaign.display_frequency || "ONCE_PER_SESSION",
  priority: campaign.priority || 0,
  scopeType: campaign.scope_type || "GLOBAL",
  branchId: campaign.branch_id || null,
  weatherLocalityId: campaign.weather_locality_id || null,
  startAt: campaign.start_at || null,
  endAt: campaign.end_at || null,
  webCartEnabled: enabled(campaign.web_cart_enabled),
  webCheckoutEnabled: enabled(campaign.web_checkout_enabled),
  mobileCartEnabled: enabled(campaign.mobile_cart_enabled),
  mobileCheckoutEnabled: enabled(campaign.mobile_checkout_enabled),
} : { ...emptyCampaign };

const platformSettings = [
  {
    key: "WEB", title: "Website", imageField: "desktopImageUrl", imageLabel: "Desktop rain-alert image",
    hint: "Recommended 1200 × 300 px", cartField: "webCartEnabled", checkoutField: "webCheckoutEnabled"
  },
  {
    key: "MOBILE", title: "Mobile App", imageField: "mobileImageUrl", imageLabel: "Mobile rain-alert image",
    hint: "Recommended 1080 × 360 px", cartField: "mobileCartEnabled", checkoutField: "mobileCheckoutEnabled"
  },
];

export default function RainAlertDisplayManager({ campaigns = [], reload, notify }) {
  const [form, setForm] = useState(() => normalizeCampaign(campaigns[0]));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaigns.length && !form.id) setForm(normalizeCampaign(campaigns[0]));
  }, [campaigns, form.id]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selectCampaign = (id) => setForm(normalizeCampaign(campaigns.find((item) => String(item.id) === String(id))));

  const uploadImage = async (file, field) => {
    if (!file) return;
    setSaving(true);
    try {
      const response = await weatherApi.uploadCampaignImage(file);
      update(field, response.data?.url || "");
      notify("success", "Image uploaded. Save changes to publish this configuration.");
    } catch (error) {
      notify("danger", error?.response?.data?.message || "Image upload failed");
    } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await weatherApi.saveCampaign(form);
      notify("success", "Rain alert display settings saved");
      setForm((current) => ({ ...current, id: current.id || response.data?.id }));
      await reload();
    } catch (error) {
      notify("danger", error?.response?.data?.message || "Unable to save display settings");
    } finally { setSaving(false); }
  };

  return <div className="rain-display-manager">
    <div className="rain-manager-head">
      <div><h5>Rain Alert Display Settings</h5><span>Choose exactly where the alert is shown on web and mobile.</span></div>
      <div className="rain-manager-meta">
        {campaigns.length > 0 && <Form.Select aria-label="Choose rain alert campaign" size="sm" value={form.id || ""} onChange={(event) => selectCampaign(event.target.value)}>
          {campaigns.map((item) => <option key={item.id} value={item.id}>{item.campaign_name}</option>)}
        </Form.Select>}
        <Form.Control aria-label="Rain alert campaign name" size="sm" value={form.campaignName} onChange={(event) => {
          const campaignName = event.target.value;
          setForm((current) => ({ ...current, campaignName, title: campaignName }));
        }} placeholder="Campaign name" />
        <Form.Select aria-label="Rain alert publishing status" size="sm" value={form.campaignStatus} onChange={(event) => update("campaignStatus", event.target.value)}>
          <option value="DRAFT">DRAFT</option><option value="ACTIVE">ACTIVE</option><option value="ARCHIVED">ARCHIVED</option>
        </Form.Select>
        <Button className="rain-primary" disabled={saving || !form.campaignName.trim()} onClick={save}>
          {saving ? <Spinner size="sm" /> : "Save changes"}
        </Button>
        <Button variant="outline-secondary" onClick={() => setForm({ ...emptyCampaign })}>New</Button>
      </div>
    </div>

    <div className="rain-config-note">Publish the campaign as Active, upload an image, then enable its pages. Customers see the image only when their branch alert is ON.</div>
    <div className="rain-simple-grid">
      {platformSettings.map((platform) => {
        const imagePath = form[platform.imageField];
        const imageUrl = getImageUrl(imagePath);
        const enabledCount = Number(form[platform.cartField]) + Number(form[platform.checkoutField]);
        return <section className="rain-platform-card" key={platform.key}>
          <div className="rain-card-title">
            <div><strong>{platform.title}</strong><small>One image is used on enabled pages</small></div>
            <Badge bg={enabledCount ? "success" : "secondary"}>{enabledCount} page{enabledCount === 1 ? "" : "s"} enabled</Badge>
          </div>
          <div className="rain-upload-box">
            {imageUrl ? <img src={imageUrl} alt={platform.imageLabel} /> : <div className="rain-empty-image">▧<span>No image uploaded</span></div>}
            <div><strong>{platform.imageLabel}</strong><small>{platform.hint}</small>
              <Form.Control type="file" accept="image/png,image/jpeg,image/webp" disabled={saving}
                onChange={(event) => uploadImage(event.target.files?.[0], platform.imageField)} />
            </div>
            {imagePath && (
              <button
                type="button"
                className="rain-remove"
                title="Remove image"
                onClick={() => update(platform.imageField, "")}
              >
                ×
              </button>
            )}
          </div>
          <div className="rain-setting-row">
            <span><strong>Show on Cart page</strong><small>Display only while the branch rain alert is active</small></span>
            <Form.Check aria-label={`${platform.title}: show rain alert on cart`} type="switch" checked={Boolean(form[platform.cartField])}
              onChange={(event) => update(platform.cartField, event.target.checked)} />
          </div>
          <div className="rain-setting-row">
            <span><strong>Show on Checkout page</strong><small>Display only while the branch rain alert is active</small></span>
            <Form.Check aria-label={`${platform.title}: show rain alert on checkout`} type="switch" checked={Boolean(form[platform.checkoutField])}
              onChange={(event) => update(platform.checkoutField, event.target.checked)} />
          </div>
          <div className="rain-simple-preview">
            {imageUrl ? <img src={imageUrl} alt={`${platform.title} rain-alert preview`} /> : <span>Upload an image to see the preview</span>}
          </div>
        </section>;
      })}
    </div>
  </div>;
}

